<script setup lang="ts">
import { ref, nextTick, useTemplateRef, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import {
  useAiStream,
  MessageRole,
  type ConversationMessage,
} from '@/Support/AI/Composables/useAiStream'
import SimpleBaseWindow from '@/Components/App/Modals/SimpleBaseWindow.vue'
import { teamData, teamPreferences, userData } from '@/Support/Users/userData'
import VueMarkdown from 'vue-markdown-render'
import { OhVueIcon } from 'oh-vue-icons'
import { AiModel, AiModels } from '@/Support/AI/Interfaces/AiModels'
import { ButtonAction } from '@/Support/Forms/Interfaces/Button'
import TextArea from '@/Components/App/Forms/TextArea.vue'
import Button from '@/Components/App/Forms/Button.vue'
import SimpleBaseModal from '@/Components/App/Modals/SimpleBaseModal.vue'
import {
  tryFetchDelete,
  tryFetchGet,
} from '@/Support/Routing/Functions/tryFetch'
import { routing } from '@/Support/Routing/Functions/routing'
import LoadingIcon from '@/Components/App/Global/LoadingIcon.vue'
import ThinkingAnimation from '@/Components/AI/ThinkingAnimation.vue'
import MarkdownItUnderline from 'markdown-it-underline'
import { t } from '@/Support/lang'
import AiConversationsList from '@/Components/App/AI/AiConversationsList.vue'
import AiResponseMenu from '@/Components/App/AI/AiResponseMenu.vue'

const prompt = ref('')
const currentModel = ref(teamPreferences.value.aiModel ?? 'gpt-5-nano')
const {
  messages,
  loading,
  chatting,
  error,
  conversationId,
  start,
  stop,
  appendUser,
  appendAssistant,
} = useAiStream()

function scrollToBottom() {
  requestAnimationFrame(() => {
    const el = document.getElementById('ai-stream-bottom')
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  })
}

const visitUrl = (url: string) => {
  router.visit(url, {
    preserveState: false,
    onError: error => {
      console.log(error)
    },
  })
}

async function send() {
  const text = prompt.value.trim()
  if (!text || loading.value) return

  appendUser(text)
  prompt.value = ''
  appendAssistant()
  await nextTick()
  scrollToBottom()

  start({
    prompt: text,
    model: currentModel.value,
    conversationId: conversationId.value,
    onMessageReceived: scrollToBottom,
    onFinish: focusPrompt,
    onLink: visitUrl,
  })
}

/**
 * Markdown
 */
const optionsMd = {
  html: true,
  linkify: true,
  typographer: true,
}

const pluginsMd = [MarkdownItUnderline]

/**
 * Modal
 */
const show = ref(false)
const modal = useTemplateRef<InstanceType<typeof SimpleBaseModal>>('modal')
const open = async (status = true) => {
  show.value = status
  showList.value = false
  if (messages.value.length === 0) {
    await loadMessages()
  }
  if (messages.value.length === 0) {
    newConversation()
    return
  }
  await nextTick()
  focusPrompt()
}
const close = () => {
  show.value = false
}
const promptArea = useTemplateRef<HTMLTextAreaElement>('promptArea')
const focusPrompt = () => promptArea.value?.focus()

/**
 * Models
 */
const aiModels = usePage().props.aiModels as AiModels
const aiModel = computed<AiModel>(() => aiModels[currentModel.value])
const changeAiModel = (model: AiModel) => {
  currentModel.value = model.value
  focusPrompt()
}

/**
 * Bubble
 */
const bubbleBg = (role: MessageRole) => {
  switch (role) {
    case MessageRole.Link:
      return 'border-accent-200 bg-accent-100 dark:bg-accent-700 hover:bg-accent-200 dark:hover:bg-accent-600'

    case MessageRole.Error:
      return 'border-danger-200 bg-danger-100 dark:bg-danger-700 hover:bg-danger-200 dark:hover:bg-danger-600'

    default:
      return 'border-neutral-200 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600'
  }
}

/**
 * Bubble click
 */
const bubbleClick = (message: ConversationMessage) => {
  if (!message.link) {
    return
  }

  visitUrl(message.link)
}

/**
 * Conversations
 */
const loadingMessages = ref(false)
const loadMessages = async () => {
  loadingMessages.value = true
  const response = await tryFetchGet(
    routing('ai.conversations.show', {
      ai_conversation: conversationId.value,
    }),
    {
      //on402: error => on402(error),
      //on403: error => on403(error),
    },
  )
  if (response) {
    messages.value = response.data as ConversationMessage[]
    if (messages.value.length && conversationId.value === null) {
      conversationId.value = messages.value[0].conversationId ?? null
    }
  }
  loadingMessages.value = false

  await nextTick()
  scrollToBottom()
}

// New Conversation

const initialMessage = computed(() => ({
  id: 'initial',
  role: MessageRole.Assistant,
  content: t('ai.query.initial-text', {
    name: userData.value.name,
  }),
}))

const newConversation = async () => {
  showList.value = false
  conversationId.value = null
  messages.value = [initialMessage.value]
  await nextTick()
  focusPrompt()
}

const cleanConversation = () => {
  messages.value = []
  if (show.value) {
    open()
  }
}

/**
 * Conversations list
 */
const showList = ref(false)
const conversationsList =
  useTemplateRef<InstanceType<typeof AiConversationsList>>('conversationsList')
const openList = async () => {
  showList.value = true
  await nextTick()
  conversationsList.value?.load()
}

const closeList = async () => {
  showList.value = false
  await conversationsList.value?.load()
  focusPrompt()
}

const loadConversation = async (conversation: string) => {
  conversationId.value = conversation
  showList.value = false
  await loadMessages()
  await nextTick(() => scrollToBottom())
}

/**
 * Remove conversation
 */
const removalLoading = ref(false)
const removeConversation = async (conversation: string) => {
  removalLoading.value = true
  await tryFetchDelete(
    routing('ai.conversations.destroy', {
      ai_conversation: conversation,
    }),
    {
      //on402: error => on402(error),
      //on403: error => on403(error),
    },
  )
  removalLoading.value = false

  if (conversation === conversationId.value) {
    conversationId.value = null
  }

  await conversationsList.value?.load()
}

defineExpose({
  open,
  cleanConversation,
})
</script>

<template>
  <simple-base-window class="bottom-0 right-0" ref="modal" :show="show">
    <AiConversationsList
      class="h-88"
      v-if="showList"
      ref="conversationsList"
      @load="loadConversation"
      :removal-loading="removalLoading"
      @remove="removeConversation"
    />
    <div
      v-else
      class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 h-96 overflow-auto bg-white dark:bg-neutral-800 rounded-t-lg"
    >
      <h2
        class="text-base font-semibold leading-6 text-neutral-900 dark:text-neutral-100"
      >
        {{ __('ai.query.title') }}
        <LoadingIcon :loading="loadingMessages || loading" class="ml-2" />
      </h2>
      <ul role="list" class="mt-6 space-y-6">
        <li
          v-for="(message, index) in messages"
          :key="index"
          class="relative flex gap-x-4"
          :class="[
            message.role === MessageRole.User ? 'justify-end' : 'justify-start',
          ]"
        >
          <div
            v-if="message.role === MessageRole.User"
            class="flex items-ends gap-2.5"
          >
            <div
              class="flex flex-col w-full max-w-[320px] leading-1.5 p-4 border-neutral-200 bg-neutral-100 rounded-ee-xl rounded-ss-xl rounded-es-2xl dark:bg-neutral-700"
            >
              <p
                class="text-sm font-normal py-2.5 text-neutral-900 dark:text-white"
              >
                {{ message.content }}
              </p>
            </div>
            <div
              class="relative flex h-6 w-6 flex-none items-center justify-center bg-white dark:bg-neutral-800"
            >
              <img
                :src="userData.profilePhotoUrl"
                alt=""
                class="relative mt-3 h-6 w-6 flex-none rounded-full bg-neutral-50 dark:bg-neutral-800"
              />
            </div>
          </div>

          <!-- AI Response-->
          <div class="flex items-start gap-2.5" v-else>
            <div class="flex flex-col justify-center items-center">
              <OhVueIcon
                name="la-robot-solid"
                class="relative mt-3 h-6 w-6 flex-none rounded-full bg-neutral-50 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-300"
              />
              <span class="text-xxxs text-neutral-600 dark:text-neutral-400">
                {{ aiModel.name }}
              </span>
            </div>
            <div
              class="flex flex-col w-full max-w-[320px] leading-1.5 p-4 rounded-ee-2xl rounded-e-xl rounded-es-xl"
              :class="bubbleBg(message.role)"
              @click="() => bubbleClick(message)"
            >
              <ThinkingAnimation v-if="message.content?.length === 0" />
              <p
                v-else
                class="text-sm font-normal py-2.5 text-neutral-900 dark:text-white"
              >
                <VueMarkdown
                  :source="message.content"
                  :options="optionsMd"
                  :plugins="pluginsMd"
                />
              </p>
            </div>
          </div>
        </li>
      </ul>
      <div id="ai-stream-bottom" />
    </div>
    <template #footer>
      <div class="flex gap-x-3 w-full p-4">
        <div class="flex-auto space-y-3">
          <div
            v-if="!showList"
            class="overflow-hidden rounded-lg shadow-xs ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-primary-400"
          >
            <label for="prompt" class="sr-only">
              {{ __('ai.query.title') }}
            </label>
            <TextArea
              ref="promptArea"
              v-model="prompt"
              rows="4"
              id="prompt"
              :disabled="loading || showList || loadingMessages"
              @keyup.enter="send"
            />
          </div>

          <div
            class="inset-x-0 bottom-0 flex justify-between items-center relative"
          >
            <AiResponseMenu
              :current-model="currentModel"
              @new="newConversation"
              @list="openList"
              @view-chat="closeList"
              @model-updated="changeAiModel"
              @reload="loadMessages"
            />
            <div class="flex space-x-2">
              <Button
                :action="ButtonAction.Cancel"
                :disabled="loading || loadingMessages"
                @click="close"
              >
                {{ __('Close') }}
              </Button>
              <Button
                :loading="loading"
                :disabled="loading || loadingMessages"
                @click="send"
              >
                {{ __('ai.query.button') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </simple-base-window>
</template>
